/**
 * WCC360 v2 — the Purpose Map.
 *
 * Interest → cause → role → career directions to explore → one small experiment.
 * These are exploration prompts, not claims about suitability. A role that
 * differs from other answers is not a contradiction; confidence reflects
 * how much evidence there is, never whether every answer agrees.
 */
import type { MatchResult } from "@/lib/scoring";
import { PASSIONS } from "@/data/passions";
import { causeById, causeNextStep, isCauseId } from "./causes";
import { MAX_ROLE_CAUSES, causeRoleById } from "./causeCatalogue";
import type { AssessmentV2State } from "./types";

export interface PurposeMapEntry {
  causeId: string;
  cause: string;
  interest: string | null;
  role: string | null;
  roleDirection: string | null;
  directions: string[];
  experiment: string | null;
  confidence: "Growing" | "Early";
  sentence: string;
}

type Part1 = Pick<AssessmentV2State["part1"], "cause_rank_ids" | "cause_roles" | "passion_ids">;

export function purposeMap(part1: Part1, results: MatchResult[] = []): PurposeMapEntry[] {
  const ranked = (part1.cause_rank_ids ?? []).slice(0, MAX_ROLE_CAUSES);
  const interests = (part1.passion_ids ?? [])
    .filter((id) => !isCauseId(id))
    .map((id) => PASSIONS.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  return ranked.flatMap((id) => {
    const cause = causeById(id);
    if (!cause) return [];
    const fams = new Set(cause.families);
    const interest = interests.find((p) => p.families.some((f) => fams.has(f))) ?? interests[0] ?? null;
    const role = causeRoleById(part1.cause_roles?.[id] ?? part1.cause_roles?.[cause.id]);
    const directions = results.filter((r) => fams.has(r.family)).slice(0, 3).map((r) => r.title);
    const step = causeNextStep(cause.id);
    const experiment = step?.quickWin ? `${step.quickWin.title}: ${step.quickWin.steps[0]}` : null;
    const evidence = [interest, role, directions.length > 0].filter(Boolean).length;
    const confidence: PurposeMapEntry["confidence"] = evidence >= 3 ? "Growing" : "Early";

    const parts: string[] = [];
    if (interest) parts.push(`You enjoy ${interest.label.toLowerCase()}`);
    parts.push(`${parts.length ? "care about" : "You care about"} ${cause.label.toLowerCase()}`);
    if (role) parts.push(`chose "${role.label.toLowerCase()}"`);
    let sentence = parts.length > 1
      ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}.`
      : `${parts[0]}.`;
    if (directions.length) sentence += ` ${directions.join(", ")} are directions you could explore today.`;
    else if (role) sentence += ` Look for work that involves ${role.direction}.`;

    return [{
      causeId: cause.id, cause: cause.label, interest: interest?.label ?? null,
      role: role?.label ?? null, roleDirection: role?.direction ?? null,
      directions, experiment, confidence, sentence,
    }];
  });
}
